import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  HEADER_HEIGHT,
  PARTS_SELECTION_CONTENTS_TOP,
  PARTS_SELECTION_FONT_SIZE,
} from '../../../../_constants/styleConst';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import SectionRow from './Section/SectionRow';
import { SectionInfo } from '../../PartSelection';
import { adjustToMaxDigits } from '../../../../_utils/utils';
import { VALID_NUMBER_DIGITS } from '../../../../_constants/Const';

type SectionTableProps = {
  sectionList: SectionInfo[];
  isShowOptionParts: boolean;
  setSectionList: React.Dispatch<React.SetStateAction<SectionInfo[]>>;
  handlePartsListImage: (isometricDrawing: string, section: string) => void;
};

export const tableHeaders = [
  { width: '2%', minWidth: '', p: '3px', label: '', textAlign: 'center' }, // 開閉アイコン
  {
    width: '8%',
    minWidth: '70px',
    p: '3px',
    label: 'LBL_SECTION',
    textAlign: 'left',
  },
  { width: '2%', minWidth: '45px', p: '3px', label: '', textAlign: 'left' }, // チェックボックス
  {
    width: '14%',
    minWidth: '130px',
    p: '10px',
    label: 'LBL_PARTS_CODE',
    textAlign: 'left',
  },
  {
    width: '16%',
    minWidth: '210px',
    p: '3px 1rem',
    label: 'LBL_PARTS_NAME',
    textAlign: 'left',
  },
  {
    width: '4%',
    minWidth: '55px',
    p: '3px',
    label: 'LBL_KEY',
    textAlign: 'center',
  },
  {
    width: '5%',
    minWidth: '70px',
    p: '3px',
    label: 'LBL_AMOUNT',
    textAlign: 'center',
  },
  {
    width: '7%',
    minWidth: '60px',
    p: '3px',
    label: 'LBL_R_CODE',
    textAlign: 'center',
  },
  {
    width: '23%',
    minWidth: '120px',
    p: '3px',
    label: 'LBL_R_DETAIL',
    textAlign: 'left',
  },
  {
    width: '7%',
    minWidth: '80px',
    p: '3px',
    label: 'LBL_PARTS_LIST',
    textAlign: 'center',
  },
  {
    width: '12%',
    minWidth: '170px',
    p: '3px',
    label: 'LBL_ECN',
    textAlign: 'center',
  },
];

/** セクション単位のテーブル */
const SectionTable: React.FC<SectionTableProps> = ({
  sectionList,
  isShowOptionParts,
  setSectionList,
  handlePartsListImage,
}) => {
  const { t } = useTranslation();
  // セクションの開閉状態
  const [expanded, setExpanded] = useState<boolean[]>([]);

  useEffect(() => {
    setExpanded(new Array(sectionList.length).fill(false));
  }, [sectionList.length]);

  /** パーツの選択状態を変更 */
  const onChangeSelection = (
    isChecked: boolean,
    listNo: string,
    inFigureNo: string,
  ) => {
    setSectionList((prev) => {
      const newState = prev.map((section) => ({
        ...section,
        partsList: section.partsList.map((part) => {
          if (part.listNo === listNo && part.inFigureNo === inFigureNo) {
            return { ...part, isSelect: isChecked };
          }
          return part;
        }),
      }));

      return newState;
    });
  };

  /** 部品コードを変更 */
  const onChangePartsCode = (
    newPartsCode: string,
    listNo: string,
    inFigureNo: string,
  ) => {
    setSectionList((prev) => {
      const newState = prev.map((section) => ({
        ...section,
        partsList: section.partsList.map((part) => {
          if (part.listNo === listNo && part.inFigureNo === inFigureNo) {
            return { ...part, selectedPartsCode: newPartsCode };
          }
          return part;
        }),
      }));

      return newState;
    });
  };

  /** 数量を変更 */
  const onChangeAmount = (
    newAmount: string,
    listNo: string,
    inFigureNo: string,
  ) => {
    setSectionList((prev) => {
      const newState = prev.map((section) => ({
        ...section,
        partsList: section.partsList.map((part) => {
          if (
            part.listNo === listNo &&
            part.inFigureNo === inFigureNo &&
            !isNaN(Number(newAmount))
          ) {
            return {
              ...part,
              amount: adjustToMaxDigits(
                Math.floor(Number(newAmount)),
                VALID_NUMBER_DIGITS.THREE,
              ),
            };
          }
          return part;
        }),
      }));

      return newState;
    });
  };

  /** アコーディオンの開閉状態を変更 */
  const toggleAccordion = (index: number) => {
    const newState = [...expanded];
    newState[index] = !expanded[index];
    setExpanded(newState);

    // 開く場合はそのセクションのアイソメ図を表示
    if (newState[index]) {
      const { name, partsList } = sectionList[index];
      handlePartsListImage(partsList[0].isometricDrawing, name);
    }
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        style={{
          backgroundColor: 'transparent',
        }}
        sx={{
          height: `calc(100dvh - (${HEADER_HEIGHT} + ${PARTS_SELECTION_CONTENTS_TOP} + 70px))`,
          overflow: 'auto',
        }}
      >
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {tableHeaders.map(
                ({ width, minWidth, p, textAlign, label }, i) => (
                  <TableCell
                    key={`parts_selection_header_${i}`}
                    sx={{
                      fontSize: PARTS_SELECTION_FONT_SIZE,
                      width,
                      minWidth,
                      p,
                      py: '10px',
                      textAlign,
                    }}
                  >
                    {t(label)}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          {sectionList.length > 0 && (
            <TableBody>
              {sectionList.map((section, sectionIndex) => {
                return (
                  section.partsList.length > 0 && (
                    <SectionRow
                      key={`${section.name}_${sectionIndex}`}
                      sectionIndex={sectionIndex}
                      section={section}
                      isExpanded={expanded[sectionIndex]}
                      isShowOptionParts={isShowOptionParts}
                      toggleAccordion={toggleAccordion}
                      handlePartsListImage={handlePartsListImage}
                      onChangeSelection={onChangeSelection}
                      onChangePartsCode={onChangePartsCode}
                      onChangeAmount={onChangeAmount}
                    />
                  )
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};
export default SectionTable;
